import { humaniseUnknownValue, warnUnknownValueOnce } from '../unknown-value'

import type { Tone } from './tone'

/**
 * One domain status, fully described: what it reads as and what colour it is.
 *
 * Each domain exports **one** record of these as the single canonical owner of
 * its status colours — composed plainly, no factory and no registry (ADR 0020
 * §5). Closed unions (a real `z.enum`) key an exhaustive `Record` so `tsc`
 * fails on a missing member; open unions go through {@link resolvePillPreset}.
 */
export type PillPreset = {
	label: string
	tone: Tone
	/** Optional leading node, passed straight to `Pill`'s `icon`. */
	icon?: React.ReactNode
}

/**
 * A preset table for an **open** union, and the only thing
 * {@link resolvePillPreset} accepts.
 *
 * The wrapper is the point. ADR 0020 §5 splits domains in two — closed unions
 * get an exhaustive `Record` so `tsc` fails on a missing member; open unions
 * get a lookup with a fallback. A bare `Record<string, PillPreset>` parameter
 * would leave that split doc-enforced only, because a
 * `Record<CaseStatus, PillPreset>` is structurally a `Record<string, …>` and
 * would pass — silently trading its compile error for a grey pill. Requiring
 * this shape makes routing a closed union through the fallback a type error,
 * and opting one in a deliberate, visible act.
 */
export type OpenPresets = {
	readonly domain: string
	readonly presets: Record<string, PillPreset>
}

/**
 * Declare a domain's presets reachable by **runtime lookup** — i.e. the backing
 * column can hand us something the record does not list.
 *
 * The house idiom, for closed and open domains alike, is two lines:
 *
 * ```ts
 * const PRESETS: Record<CaseStatus, PillPreset> = { … }  // tsc: exhaustive
 * export const CASE_STATUS_PRESETS = openPresets('case status', PRESETS)
 * ```
 *
 * Both halves earn their line, and neither replaces the other. The annotation
 * is checked *before* this call widens the key type, so wrapping costs a closed
 * union nothing: a new `z.enum` member is still a compile error. What the
 * wrapper adds is the only defence against the fact that **none of the backing
 * columns are real enums** — they are `VARCHAR`/`Text` with the enum enforced in
 * application code only.
 *
 * A domain that is genuinely open (severity — free text, no `z.enum` to
 * annotate against) simply has no first line.
 *
 * @param domain Named in the dev warning — e.g. `'severity'`. It travels with
 * the presets rather than being repeated at every call site.
 */
export function openPresets(
	domain: string,
	presets: Record<string, PillPreset>,
): OpenPresets {
	return { domain, presets }
}

/**
 * Look a raw runtime value up in an open preset table, falling back to a
 * neutral pill with a humanised label.
 *
 * The fallback is not paranoia: **none of the backing database columns are real
 * enums** — they are `VARCHAR`/`Text` with the enum enforced only in
 * application code. This is the difference between a surprise value rendering
 * as a grey pill and the cell rendering blank (P-17).
 *
 * Lookup is case- and whitespace-insensitive because stored values are not
 * reliably lower-cased (see GOTCHAS on `alert_severity`).
 *
 * A closed union must not arrive here **by accident** — that is what the
 * {@link OpenPresets} brand prevents. Opting one in deliberately is expected
 * and is the house idiom: annotate the record to the union (so `tsc` fails on a
 * missing member), then hand it to {@link openPresets} (so a value the
 * `VARCHAR` invents still paints). See {@link openPresets}.
 */
export function resolvePillPreset(
	value: string | null | undefined,
	table: OpenPresets,
): PillPreset {
	const normalised = value?.trim().toLowerCase() ?? ''
	const preset = table.presets[normalised]
	if (preset) return preset

	if (import.meta.env.DEV && normalised)
		warnUnknownValueOnce(table.domain, normalised)

	return {
		label: normalised ? humaniseUnknownValue(normalised) : 'Unknown',
		tone: 'neutral',
	}
}
